import React, { useEffect, useMemo, useState } from 'react';

import { Flex, Tag } from '@zillow/constellation';
import { Text } from '@zillow/constellation-10';

import { STEP_TO_STEP_GROUP_MAP, SUB_STEP_MAP } from '../../../../containers/CreateListingPage/config';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';

import ProgressStepper from '../../../../components/Stepper/ProgressStepper';

import Step from '../../../../components/Stepper/Step';

import { DEFAULT_STEP } from '../../../../containers/CreateListingPage';

import type { WizardStepType } from '../../../Wizard/types';

type Props = {
  status: keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;
  step: string;
};

type ProgressStep = {
  stepKey: string;
};

const renderStatusTag = (status = '') => {
  switch (status) {
    case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING:
      return 'COMPLETE';

    // fallthrough
    case LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE:
    default:
      return 'INCOMPLETE';
  }
};

const ProgressStepperSummary = ({ status, step }: Props) => {
  const [subStepIndexMap, setSubStepIndexMap] = useState<{ [key: string]: number }>({});
  const [renderedStepList, setRenderedStepList] = useState<Array<React.ReactNode>>();

  useEffect(() => {
    initState();
  }, []);

  /**
   * find a valid progress step key from within the possible step group
   * legacy listings might have invalid step keys they are resuming from
   */
  const progressStepKey = useMemo(() => {
    const validStep =
      (STEP_TO_STEP_GROUP_MAP[step.toUpperCase()] as WizardStepType) ||
      (STEP_TO_STEP_GROUP_MAP[DEFAULT_STEP] as WizardStepType);

    const { progressStepKey }: Pick<WizardStepType, 'progressStepKey'> = validStep;

    return progressStepKey;
  }, []);

  const stepList: Array<WizardStepType> | [] = SUB_STEP_MAP.get(progressStepKey) || [];

  const currentStepIndex =
    stepList.findIndex((progressStep: ProgressStep) => progressStep.stepKey === step.toUpperCase()) + 1 || 1;
  const activeIndex = subStepIndexMap[progressStepKey];
  const statusTag = renderStatusTag(status);

  const initState = () => {
    const tempStepList = [];
    const tempMap: { [key: string]: number } = {};

    let index = 0;
    for (const item of SUB_STEP_MAP.entries()) {
      const [key] = item;
      tempMap[key] = index;
      // @ts-expect-error
      tempStepList.push(<Step key={key} />);

      index += 1;
    }

    setSubStepIndexMap(tempMap);
    setRenderedStepList(tempStepList);
  };

  return (
    <Flex data-testid="ProgressStepperSummary" display="flex" flexDirection="column">
      <Flex
        data-testid="PSS-HeaderContainer"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        paddingBottom="xs"
      >
        <Text textStyle="body-bold">Listing setup</Text>
        <Tag appearance="gray">{statusTag}</Tag>
      </Flex>

      <div data-testid="ProgressStepperContainer">
        {/* @ts-expect-error */}
        <ProgressStepper
          currentStepIndex={currentStepIndex}
          activeIndex={activeIndex}
          totalSteps={stepList.length}
          left={-55}
          right={45}
          showDesktopViewBreakpoint="xxl"
          alwaysShowProgressMeterTitle={true}
        >
          {renderedStepList}
        </ProgressStepper>
      </div>
    </Flex>
  );
};

export default ProgressStepperSummary;
